import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Presentation, Download, Upload, Trash2, FileText, Info, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";

export default function AdminPreviewPlatform() {
  const [status, setStatus] = useState({ has_uploaded: false, size_bytes: 0 });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const confirm = useConfirm();
  // Pitch format settings
  const [programme, setProgramme] = useState({});
  const [pitchMin, setPitchMin] = useState("");
  const [pitchQAMin, setPitchQAMin] = useState("");
  const [pitchBusy, setPitchBusy] = useState(false);
  // Team count toggle (3 or 6)
  const [teamCount, setTeamCount] = useState(3);
  const [teamCountBusy, setTeamCountBusy] = useState(false);
  // Sandwich choices (registration food picker source)
  const [sandwich, setSandwich] = useState("");   // textarea, one per line
  const [sandwichBusy, setSandwichBusy] = useState(false);
  // Exit survey reminder
  const [reminderBusy, setReminderBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUsers, setPreviewUsers] = useState([]);
  const [excludeIds, setExcludeIds] = useState(new Set());
  const [lastRecipients, setLastRecipients] = useState(null);

  const openReminderPreview = async () => {
    setReminderBusy(true);
    try {
      const { data } = await api.get("/admin/exit-survey-incomplete");
      setPreviewUsers(data.users || []);
      setExcludeIds(new Set());
      setPreviewOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load list");
    } finally { setReminderBusy(false); }
  };

  const toggleExclude = (uid) => {
    setExcludeIds((prev) => {
      const next = new Set(prev);
      if (next.has(uid)) next.delete(uid); else next.add(uid);
      return next;
    });
  };

  const loadProgramme = async () => {
    try {
      const { data } = await api.get("/programme");
      setProgramme(data || {});
      setPitchMin(data?.pitch_minutes != null ? String(data.pitch_minutes) : "");
      setPitchQAMin(data?.pitch_qa_minutes != null ? String(data.pitch_qa_minutes) : "");
      setTeamCount(Number(data?.team_count) === 6 ? 6 : 3);
      setSandwich((data?.sandwich_choices || []).join("\n"));
    } catch { /* non-fatal */ }
  };

  const saveSandwich = async () => {
    setSandwichBusy(true);
    try {
      const opts = sandwich.split("\n").map((s) => s.trim()).filter(Boolean);
      await api.put("/programme", { sandwich_choices: opts });
      toast.success(`Sandwich list saved · ${opts.length} option${opts.length === 1 ? "" : "s"}`);
      loadProgramme();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save sandwich list");
    } finally { setSandwichBusy(false); }
  };

  const saveTeamCount = async (next) => {
    setTeamCountBusy(true);
    try {
      await api.put("/programme", { team_count: next });
      toast.success(`Programme set to ${next} teams`);
      setTeamCount(next);
      loadProgramme();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setTeamCountBusy(false); }
  };

  const savePitchFormat = async () => {
    setPitchBusy(true);
    try {
      const payload = {
        pitch_minutes: pitchMin === "" ? null : Number(pitchMin),
        pitch_qa_minutes: pitchQAMin === "" ? null : Number(pitchQAMin),
      };
      await api.put("/programme", payload);
      toast.success("Final-pitch format saved");
      loadProgramme();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setPitchBusy(false); }
  };

  const sendSurveyReminder = async () => {
    setReminderBusy(true);
    try {
      const payload = excludeIds.size > 0 ? { exclude_user_ids: Array.from(excludeIds) } : {};
      const { data } = await api.post("/admin/exit-survey-reminder", payload);
      if (data.sent === 0) {
        toast.success(data.message || "Everyone is already done");
      } else {
        toast.success(`Reminder sent to ${data.sent} student${data.sent === 1 ? "" : "s"}`);
      }
      setLastRecipients(data.recipients || []);
      setPreviewOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally { setReminderBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/admin/webinar-deck/status");
        if (!cancelled) setStatus(data);
      } catch { /* non-fatal */ }
    })();
    loadProgramme();
    return () => { cancelled = true; };
  }, []);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/admin/webinar-deck/status");
      setStatus(data);
    } catch { /* non-fatal */ }
  };

  const downloadDeck = async (source) => {
    setBusy(true);
    try {
      const res = await api.get(`/admin/webinar-deck/download?source=${source}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.presentationml.presentation" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = source === "uploaded" ? "MBA-Workbook-Webinar-custom.pptx" : "MBA-Workbook-Webinar-default.pptx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Deck downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setBusy(false); }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pptx")) {
      toast.error("Please upload a .pptx file");
      e.target.value = "";
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/webinar-deck/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Uploaded — ${prettyBytes(data.size_bytes)}`);
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  const removeUploaded = async () => {
    if (!(await confirm({
      title: "Remove the uploaded deck?",
      description: "Downloads will fall back to the default platform-introduction deck. You can re-upload later anytime.",
      confirmText: "Remove",
      destructive: true,
    }))) return;
    setBusy(true);
    try {
      await api.delete("/admin/webinar-deck/uploaded");
      toast.success("Uploaded deck removed — downloads now serve the default");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    } finally { setBusy(false); }
  };

  const [lockStatus, setLockStatus] = useState({ selections_unlocked: true });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/programme/selection-status");
        if (!cancelled) setLockStatus(data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleSelections = async () => {
    const goingToLock = lockStatus.selections_unlocked;
    if (!(await confirm({
      title: goingToLock ? "Lock student selections?" : "Unlock student selections?",
      description: goingToLock
        ? "Students will no longer be able to claim a founding business or target business. Existing claims stay in place. Use this if you want to freeze the platform mid-week."
        : "Students will be able to claim a founding business and target businesses immediately. Do this at 10:00 on Monday 15 June when the simulation starts.",
      confirmText: goingToLock ? "Lock selections" : "Unlock selections",
      destructive: goingToLock,
    }))) return;
    setBusy(true);
    try {
      const path = goingToLock ? "/admin/selections/lock" : "/admin/selections/unlock";
      const { data } = await api.post(path);
      setLockStatus(data);
      toast.success(goingToLock ? "Selections locked" : "Selections unlocked — students can now claim");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not toggle selections");
    } finally { setBusy(false); }
  };

  const releaseAllBusinesses = async () => {
    if (!(await confirm({
      title: "Release ALL founding business claims?",
      description: "Wipes the founding business off every team and unlocks each business so it can be claimed again. Use this to clear pre-week dry runs. Target-business claims are NOT touched — use the button below for those.",
      confirmText: "Release everything",
      destructive: true,
    }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/businesses/release-all");
      toast.success(`Released ${data.released} founding business claim${data.released === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release");
    } finally { setBusy(false); }
  };

  const releaseAllTargets = async () => {
    if (!(await confirm({
      title: "Release ALL target business claims?",
      description: "Clears every target business shortlist hold and final pick across every team — including any 'finalised' commits. Use this to wipe pre-week dry runs of the acquisition candidate flow.",
      confirmText: "Release every target",
      destructive: true,
    }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/target-businesses/release-all");
      toast.success(`Released ${data.released} target business claim${data.released === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not release");
    } finally { setBusy(false); }
  };

  const resetTeam = async (teamLabel) => {
    if (!(await confirm({
      title: `Reset ${teamLabel} back to Day 0?`,
      description: "Clears the founding business, target business, vision statement, sector, and all members' C-suite roles. Members stay on the team. Use this before a demo run.",
      confirmText: "Reset team",
      destructive: true,
    }))) return;
    setBusy(true);
    try {
      const { data: teams } = await api.get("/teams");
      const wantedCode = teamLabel.replace(/\s+/g, "").toUpperCase();
      const t = (teams || []).find((x) => x.join_code === wantedCode || x.name === teamLabel);
      if (!t) throw new Error(`${teamLabel} not found`);
      await api.post(`/admin/teams/${t.id}/reset`);
      toast.success(`${teamLabel} reset to clean Day 0 state`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Could not reset team");
    } finally { setBusy(false); }
  };


  const seedDemoBookings = async () => {
    if (!(await confirm({
      title: "Seed demo mentor bookings?",
      description: "Creates 2 demo bookings on Clare Cracknell so the Reserve / Release-early / Swap-late buttons are visible on the mentor dashboard. Real bookings are untouched.",
      confirmText: "Seed demo data",
    }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mentor-slots/seed-demo");
      toast.success(`Demo bookings created (${data.seeded}) on ${data.mentor}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not seed demo bookings");
    } finally { setBusy(false); }
  };

  const resetDemoBookings = async () => {
    if (!(await confirm({
      title: "Remove demo mentor bookings?",
      description: "Clears any mentor slot marked as [DEMO]. Real bookings are untouched.",
      confirmText: "Remove demo data",
      destructive: true,
    }))) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/mentor-slots/reset-demo");
      toast.success(data.cleared ? `Cleared ${data.cleared} demo booking${data.cleared === 1 ? "" : "s"}` : "No demo bookings to clear");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not clear demo bookings");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-8" data-testid="admin-preview-platform-page">
      <div>
        <div className="label-eyebrow flex items-center gap-2">
          <Presentation size={12} /> Admin · preview the platform
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-2">
          Webinar deck
        </h1>
        <p className="text-[#525252] mt-3 max-w-3xl text-sm leading-relaxed">
          Two-deck system to introduce the platform to students or sell it to new cohorts.
          The <strong>default deck</strong> is regenerated from the current platform features every time you click download —
          so it never goes stale. The <strong>custom deck</strong> is a PPTX you upload (after tailoring for a
          specific audience). When a custom deck exists, the &quot;auto&quot; download serves it; otherwise it falls back to the default.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {/* Default deck card */}
        <div className="border-2 border-[#0A0A0A] bg-white p-6 flex flex-col gap-4" data-testid="default-deck-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-eyebrow flex items-center gap-2"><FileText size={12} /> Deck #1</div>
              <div className="font-display text-2xl font-bold tracking-tight mt-1">Default deck</div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] bg-[#0A0A0A] text-white px-2 py-1 font-semibold">AUTO-BUILT</div>
          </div>
          <p className="text-sm text-[#525252] leading-relaxed">
            A polished 22-slide PPTX generated server-side, with speaker notes on every slide. Mirrors the current platform
            (login flow, target-business 10-min/15:55 rules, CEO Report AI polish, mentor booking rules, framework tools,
            surveys, retrospective). Image placeholders flag where to drop screenshots; the structure and copy are ready to present.
          </p>
          <div className="border-l-4 border-[#2596be] bg-[#E6F4FA] px-3 py-2 text-xs text-[#1e7da3] leading-snug flex items-start gap-2">
            <Info size={14} className="shrink-0 mt-0.5" />
            <span>This file updates automatically every time we ship platform changes — just re-download.</span>
          </div>
          <div className="mt-auto flex flex-wrap gap-2">
            <Button
              onClick={() => downloadDeck("default")}
              disabled={busy}
              className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]"
              data-testid="download-default-deck-btn"
            >
              <Download size={14} className="mr-2" /> Download default deck
            </Button>
          </div>
        </div>

        {/* Custom deck card */}
        <div className="border-2 border-[#0A0A0A] bg-white p-6 flex flex-col gap-4" data-testid="custom-deck-card">
          <div className="flex items-start justify-between">
            <div>
              <div className="label-eyebrow flex items-center gap-2"><Upload size={12} /> Deck #2</div>
              <div className="font-display text-2xl font-bold tracking-tight mt-1">Custom deck</div>
            </div>
            {status.has_uploaded
              ? <div className="text-[10px] uppercase tracking-[0.16em] bg-[#A23232] text-white px-2 py-1 font-semibold flex items-center gap-1" data-testid="custom-deck-status-loaded"><CheckCircle2 size={11} /> UPLOADED</div>
              : <div className="text-[10px] uppercase tracking-[0.16em] border border-[#0A0A0A] text-[#0A0A0A] px-2 py-1 font-semibold" data-testid="custom-deck-status-empty">NONE YET</div>}
          </div>
          <p className="text-sm text-[#525252] leading-relaxed">
            Upload a .pptx tailored for a specific audience (e.g. school admins, faculty showcase, partner pitch).
            While uploaded, the &quot;Download for use&quot; button serves <em>your version</em> — handy if you want to share
            the same link consistently.
          </p>
          {status.has_uploaded && (
            <div className="border-l-4 border-[#A23232] bg-[#FDEDED] px-3 py-2 text-xs text-[#7A1F1F] leading-snug">
              Current custom deck size: <strong>{prettyBytes(status.size_bytes)}</strong>
            </div>
          )}
          <div className="mt-auto flex flex-wrap gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".pptx"
              onChange={onUpload}
              className="hidden"
              data-testid="custom-deck-file-input"
            />
            <Button
              onClick={() => fileRef.current?.click()}
              disabled={busy}
              className="rounded-none bg-[#A23232] hover:bg-[#8a2828]"
              data-testid="upload-custom-deck-btn"
            >
              <Upload size={14} className="mr-2" /> {status.has_uploaded ? "Replace upload" : "Upload .pptx"}
            </Button>
            {status.has_uploaded && (
              <>
                <Button
                  variant="outline"
                  onClick={() => downloadDeck("uploaded")}
                  disabled={busy}
                  className="rounded-none border-[#0A0A0A]"
                  data-testid="download-custom-deck-btn"
                >
                  <Download size={14} className="mr-2" /> Download custom
                </Button>
                <Button
                  variant="outline"
                  onClick={removeUploaded}
                  disabled={busy}
                  className="rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED]"
                  data-testid="remove-custom-deck-btn"
                >
                  <Trash2 size={14} className="mr-2" /> Remove
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Use-for-presentation block */}
      <div className="border border-[#0A0A0A] bg-[#F5F5F5] p-5" data-testid="use-deck-block">
        <div className="label-eyebrow">For presenting</div>
        <p className="text-sm mt-2 max-w-3xl">
          One-click download of whichever deck is &quot;live&quot; — your custom one if uploaded, the default otherwise:
        </p>
        <div className="mt-3">
          <Button
            onClick={() => downloadDeck("auto")}
            disabled={busy}
            className="rounded-none bg-[#0A0A0A] hover:bg-[#222]"
            data-testid="download-auto-deck-btn"
          >
            <Download size={14} className="mr-2" /> Download for use
          </Button>
        </div>
      </div>

      {/* Reset a team back to Day 0 — for clean demo runs */}
      {/* SELECTION LOCK — controls whether students can claim founding/target businesses */}
      <div
        className={`border-2 p-5 md:p-6 ${lockStatus.selections_unlocked ? "border-[#0A8C5C] bg-[#F4FBF7]" : "border-[#A23232] bg-[#FDEDED]"}`}
        data-testid="selection-lock-block"
      >
        <div className="flex flex-col md:flex-row md:items-start gap-5">
          <div className={`shrink-0 w-12 h-12 flex items-center justify-center ${lockStatus.selections_unlocked ? "bg-[#0A8C5C] text-white" : "bg-[#A23232] text-white"}`}>
            <Lock size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className={`label-eyebrow ${lockStatus.selections_unlocked ? "text-[#0A8C5C]" : "text-[#7A1F1F]"}`}>
              Pre-week control · selection lock
            </div>
            <h2 className="font-display text-2xl md:text-3xl font-black tracking-tighter mt-1">
              {lockStatus.selections_unlocked ? "Selections are OPEN" : "Selections are LOCKED"}
            </h2>
            <p className="text-sm text-[#0A0A0A] mt-2 max-w-3xl leading-relaxed">
              {lockStatus.selections_unlocked
                ? "Students can claim a founding business and target businesses. Lock again if you need to freeze the platform mid-week."
                : "Students can register, join teams and explore content, but the Claim buttons on Founding Businesses and Target Businesses are greyed out. Unlock when the simulation officially begins at 10:00 on Monday 15 June 2026."}
            </p>
            <p className="text-xs text-[#525252] mt-2">
              Suggested kick-off: <strong>10:00, Monday 15 June 2026 (BST)</strong>. Toggle is manual — no timer is in play.
            </p>
            <Button
              onClick={toggleSelections}
              disabled={busy}
              className={`mt-4 rounded-none h-11 px-6 ${lockStatus.selections_unlocked ? "bg-[#A23232] hover:bg-[#7A1F1F] text-white" : "bg-[#0A8C5C] hover:bg-[#075a3b] text-white"}`}
              data-testid="selection-lock-toggle"
            >
              <Lock size={14} className="mr-2" />
              {lockStatus.selections_unlocked ? "Lock selections" : "Unlock selections (start the week)"}
            </Button>
          </div>
        </div>
      </div>

      {/* Release ALL founding businesses — for pre-week dry-run cleanup */}
      <div className="border-2 border-[#0A0A0A] bg-[#FEF3D0] p-5 md:p-6" data-testid="release-all-businesses-block">
        <div className="label-eyebrow text-[#7A5B17]">Pre-week cleanup · founding businesses</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Release all founding business claims</h2>
        <p className="text-sm text-[#0A0A0A] mt-2 max-w-3xl leading-relaxed">
          Clears the founding business off every team and unlocks each business so it can be claimed again.
          Target-business shortlists are <strong>NOT</strong> touched — use the next button for those.
        </p>
        <Button
          onClick={releaseAllBusinesses}
          disabled={busy}
          variant="outline"
          className="mt-4 rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED] h-11"
          data-testid="release-all-businesses-btn"
        >
          <Trash2 size={14} className="mr-2" /> Release ALL founding businesses
        </Button>
      </div>

      {/* Release ALL target businesses — Verdi Clinics & friends */}
      <div className="border-2 border-[#0A0A0A] bg-[#FEF3D0] p-5 md:p-6" data-testid="release-all-targets-block">
        <div className="label-eyebrow text-[#7A5B17]">Pre-week cleanup · target businesses</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Release all target business claims</h2>
        <p className="text-sm text-[#0A0A0A] mt-2 max-w-3xl leading-relaxed">
          Clears every shortlist hold and final pick across all teams — including anything that has already
          been finalised. Use this when a target business (e.g. <em>Verdi Clinics</em>) is stuck as
          &quot;claimed&quot; from a dry run and you want a clean slate for the live week.
        </p>
        <Button
          onClick={releaseAllTargets}
          disabled={busy}
          variant="outline"
          className="mt-4 rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED] h-11"
          data-testid="release-all-targets-btn"
        >
          <Trash2 size={14} className="mr-2" /> Release ALL target businesses
        </Button>
      </div>


      <div className="border-2 border-[#0A0A0A] bg-[#FBE7A8] p-5 md:p-6" data-testid="reset-team-block">
        <div className="label-eyebrow text-[#7A5B17]">Demo · reset a team</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Wipe a team back to Day 0</h2>
        <p className="text-sm text-[#0A0A0A] mt-2 max-w-3xl leading-relaxed">
          Clears the founding business, target business, vision statement, sector, and every
          member&apos;s C-suite role. Members stay on the team. Use this before re-recording the student journey.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          {["Team 1", "Team 2", "Team 3"].map((label) => (
            <Button
              key={label}
              onClick={() => resetTeam(label)}
              disabled={busy}
              variant="outline"
              className="rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED]"
              data-testid={`reset-team-btn-${label.replace(/\s+/g, "").toLowerCase()}`}
            >
              <Trash2 size={14} className="mr-2" /> Reset {label}
            </Button>
          ))}
        </div>
      </div>


      {/* Demo mentor bookings — for live training / showing off the Reserve flow */}
      <div className="border-2 border-[#0A0A0A] bg-[#FEF3D0] p-5 md:p-6" data-testid="demo-bookings-block">
        <div className="label-eyebrow text-[#7A5B17]">Live demo · mentor reserve flow</div>
        <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Demo mentor bookings</h2>
        <p className="text-sm text-[#0A0A0A] mt-2 max-w-3xl leading-relaxed">
          Creates two bookings on <strong>Clare Cracknell</strong> so the Reserve / Release-early / Swap-late buttons
          are visible on the mentor dashboard for training and walkthroughs:
        </p>
        <ul className="text-sm text-[#0A0A0A] mt-2 list-disc pl-5 space-y-1">
          <li>Slot 1 — Team 1 primary <strong>+ Team 2 reserve</strong> (both buttons visible).</li>
          <li>Slot 2 — Team 1 primary, no reserve (only Release-early visible).</li>
        </ul>
        <p className="text-xs text-[#7A5B17] mt-3">
          Real bookings are never touched — only slots marked as <code className="bg-[#FBE7A8] px-1.5">[DEMO]</code> are affected.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={seedDemoBookings}
            disabled={busy}
            className="rounded-none bg-[#0A0A0A] hover:bg-[#222] text-[#f4b847]"
            data-testid="seed-demo-bookings-btn"
          >
            <Upload size={14} className="mr-2" /> Seed demo bookings
          </Button>
          <Button
            onClick={resetDemoBookings}
            disabled={busy}
            variant="outline"
            className="rounded-none border-[#A23232] text-[#A23232] hover:bg-[#FDEDED]"
            data-testid="reset-demo-bookings-btn"
          >
            <Trash2 size={14} className="mr-2" /> Remove demo bookings
          </Button>
        </div>
      </div>

      {/* Team count (3 or 6) */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="admin-team-count-panel">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252]">Programme size</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Team count</h2>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl leading-relaxed">
            Autumn typically runs with <strong>6 teams</strong> (two teams per sponsor challenge, competing). Spring often runs with <strong>3 teams</strong> (one team per challenge). Flip between them — extra team rows are created on the fly, none are deleted.
          </p>
        </div>
        <div className="flex gap-3">
          <button type="button" onClick={() => saveTeamCount(3)} disabled={teamCountBusy}
            data-testid="team-count-3-btn"
            className={`flex-1 border-2 p-5 text-left transition-colors ${teamCount === 3 ? "border-[#25AAAA] bg-[#E6F7F7]" : "border-[#E5E7EB] bg-white hover:border-[#25AAAA]"}`}>
            <div className="font-display text-3xl font-black tracking-tight text-[#0F1F2E]">3 teams</div>
            <div className="text-xs text-[#525252] mt-1">One team per sponsor challenge · Spring default</div>
            {teamCount === 3 && <div className="mt-2 text-[10px] uppercase tracking-wider text-[#25AAAA] font-semibold">✓ Active</div>}
          </button>
          <button type="button" onClick={() => saveTeamCount(6)} disabled={teamCountBusy}
            data-testid="team-count-6-btn"
            className={`flex-1 border-2 p-5 text-left transition-colors ${teamCount === 6 ? "border-[#25AAAA] bg-[#E6F7F7]" : "border-[#E5E7EB] bg-white hover:border-[#25AAAA]"}`}>
            <div className="font-display text-3xl font-black tracking-tight text-[#0F1F2E]">6 teams</div>
            <div className="text-xs text-[#525252] mt-1">Two teams per sponsor · Autumn default</div>
            {teamCount === 6 && <div className="mt-2 text-[10px] uppercase tracking-wider text-[#25AAAA] font-semibold">✓ Active</div>}
          </button>
        </div>
      </div>

      {/* Programme end date — drives GDPR consent wording and the data-retention statement */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-3" data-testid="admin-programme-end-panel">
        <div>
          <div className="label-eyebrow">GDPR · contact period</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Programme end date</h2>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl leading-relaxed">
            Students consent to be emailed <strong>until this date</strong>. It appears in the registration consent text,
            the privacy notice and every email footer. Run Data Reset after it passes.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input type="date" value={programme.end_date || ""} data-testid="admin-programme-end-input"
            onChange={(e) => setProgramme((p) => ({ ...p, end_date: e.target.value }))}
            className="border border-[#0A0A0A] h-10 px-3 text-sm rounded-none" />
          <Button onClick={async () => { try { await api.put("/programme", { end_date: programme.end_date }); toast.success("Programme end date saved"); } catch { toast.error("Could not save"); } }}
            className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]" data-testid="admin-programme-end-save">Save</Button>
        </div>
      </div>

      {/* Sandwich choices (registration food picker) */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="admin-sandwich-panel">
        <div>
          <div className="label-eyebrow text-[#7A5B17]">Mission Café · sandwich list</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Sandwich choices at registration</h2>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl leading-relaxed">
            One option per line. Paste the caterer&apos;s current list — students see this
            list <strong>at registration</strong> and pick one for the sandwich sessions.
            Leaving this empty hides the sandwich step from the registration form.
          </p>
        </div>
        <textarea
          data-testid="admin-sandwich-textarea"
          value={sandwich}
          onChange={(e) => setSandwich(e.target.value)}
          rows={Math.max(4, sandwich.split("\n").length + 1)}
          placeholder={"Chicken & bacon\nHalloumi & falafel\nEgg mayo\nHummus & roasted veg"}
          className="w-full border border-[#0A0A0A] p-3 text-sm font-mono resize-y rounded-none"
        />
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="text-xs text-[#525252]">
            {sandwich.split("\n").map((s) => s.trim()).filter(Boolean).length} option
            {sandwich.split("\n").map((s) => s.trim()).filter(Boolean).length === 1 ? "" : "s"}
          </div>
          <Button
            onClick={saveSandwich}
            disabled={sandwichBusy}
            className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]"
            data-testid="admin-sandwich-save-btn"
          >
            {sandwichBusy ? "Saving…" : "Save sandwich list"}
          </Button>
        </div>
      </div>

      {/* Final pitch format settings */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="admin-pitch-format-panel">        <div>
          <div className="label-eyebrow">Final pitch format</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Pitch &amp; Q&amp;A length</h2>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl leading-relaxed">
            Free-format minutes shown on the Final Pitch page. Default is <strong>15 minutes presenting + 10 minutes Q&amp;A</strong>. Future SKUs (Startup M&amp;A education week, corporate workshops) can run shorter or longer — just save here and the student copy updates everywhere.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="label-eyebrow !text-[#525252]" htmlFor="pitch-min-input">Presenting time (minutes)</label>
            <input
              id="pitch-min-input"
              type="number"
              min="1"
              max="120"
              value={pitchMin}
              onChange={(e) => setPitchMin(e.target.value)}
              placeholder="15"
              className="rounded-none border border-[#0A0A0A] h-11 w-full px-3 text-sm mt-1"
              data-testid="admin-pitch-minutes-input"
            />
          </div>
          <div>
            <label className="label-eyebrow !text-[#525252]" htmlFor="pitch-qa-input">Q&amp;A time (minutes)</label>
            <input
              id="pitch-qa-input"
              type="number"
              min="0"
              max="120"
              value={pitchQAMin}
              onChange={(e) => setPitchQAMin(e.target.value)}
              placeholder="10"
              className="rounded-none border border-[#0A0A0A] h-11 w-full px-3 text-sm mt-1"
              data-testid="admin-pitch-qa-minutes-input"
            />
          </div>
        </div>
        <Button
          onClick={savePitchFormat}
          disabled={pitchBusy}
          className="rounded-none bg-[#2596be] hover:bg-[#1e7da3]"
          data-testid="admin-pitch-format-save"
        >
          {pitchBusy ? "Saving…" : "Save pitch format"}
        </Button>
      </div>

      {/* Exit-survey reminder */}
      <div className="border border-[#0A0A0A] bg-white p-6 space-y-4" data-testid="admin-survey-reminder-panel">
        <div>
          <div className="label-eyebrow">Exit survey</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mt-1">Remind non-completers</h2>
          <p className="text-xs text-[#525252] mt-2 max-w-2xl leading-relaxed">
            Click <strong>Preview recipients</strong> first — you&apos;ll see the full list of every student who hasn&apos;t completed the exit survey yet, with checkboxes to exclude test/seed accounts before you fire the reminder broadcast.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            onClick={openReminderPreview}
            disabled={reminderBusy}
            className="rounded-none bg-[#f4b847] hover:bg-[#dba134] text-[#0A0A0A]"
            data-testid="admin-survey-reminder-preview"
          >
            {reminderBusy ? "Loading…" : "Preview recipients"}
          </Button>
          {lastRecipients && (
            <span className="text-xs text-[#525252]">
              Last run sent to <strong>{lastRecipients.length}</strong> student{lastRecipients.length === 1 ? "" : "s"}.
              <button onClick={() => setLastRecipients(lastRecipients)} className="ml-2 text-[#2596be] hover:underline" data-testid="admin-survey-reminder-show-last">View list</button>
            </span>
          )}
        </div>
      </div>

      {/* Recipient preview / selection dialog */}
      <Dialog open={previewOpen} onOpenChange={(o) => { if (!o) { setPreviewOpen(false); setExcludeIds(new Set()); } }}>
        <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="admin-survey-reminder-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl font-black tracking-tighter">
              Exit-survey reminder · {previewUsers.length - excludeIds.size} of {previewUsers.length} recipients
            </DialogTitle>
            <DialogDescription className="text-sm text-[#525252] leading-relaxed">
              Untick any account you don&apos;t want to remind (e.g. test accounts, seed users). The reminder will fire to everyone still ticked.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[50vh] overflow-y-auto border border-[#E5E7EB]">
            {previewUsers.length === 0 ? (
              <div className="p-8 text-center text-sm text-[#525252]">
                Everyone has completed the exit survey 🎉
              </div>
            ) : (
              previewUsers.map((u) => (
                <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-[#FAFAFA] border-b border-[#E5E7EB] last:border-b-0 cursor-pointer text-sm" data-testid={`admin-survey-recipient-${u.id}`}>
                  <input
                    type="checkbox"
                    checked={!excludeIds.has(u.id)}
                    onChange={() => toggleExclude(u.id)}
                    className="w-4 h-4 accent-[#2596be]"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{u.name || "(no name)"}</div>
                    <div className="text-xs text-[#525252] truncate">{u.email}</div>
                  </div>
                  {u.team_id ? (
                    <span className="text-[10px] uppercase tracking-wider bg-[#0A0A0A] text-white px-1.5 py-0.5">In team</span>
                  ) : (
                    <span className="text-[10px] uppercase tracking-wider bg-[#A0A0A0] text-white px-1.5 py-0.5">No team</span>
                  )}
                </label>
              ))
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button
              onClick={() => { const all = new Set(previewUsers.filter((u) => !u.team_id).map((u) => u.id)); setExcludeIds(all); }}
              disabled={previewUsers.length === 0}
              variant="outline"
              className="rounded-none border-[#0A0A0A] text-xs"
              data-testid="admin-survey-exclude-no-team"
            >
              Exclude all &quot;no team&quot; accounts
            </Button>
            <Button variant="outline" onClick={() => { setPreviewOpen(false); setExcludeIds(new Set()); }} className="rounded-none border-[#0A0A0A]" data-testid="admin-survey-reminder-cancel">Cancel</Button>
            <Button
              onClick={sendSurveyReminder}
              disabled={reminderBusy || previewUsers.length === 0 || previewUsers.length - excludeIds.size === 0}
              className="rounded-none bg-[#f4b847] hover:bg-[#dba134] text-[#0A0A0A]"
              data-testid="admin-survey-reminder-send"
            >
              {reminderBusy ? "Sending…" : `Send to ${previewUsers.length - excludeIds.size}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Last recipients dialog (post-send confirmation) */}
      <Dialog open={!!lastRecipients && !previewOpen} onOpenChange={(o) => { if (!o) setLastRecipients(null); }}>
        <DialogContent className="rounded-none max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="admin-survey-last-recipients-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black tracking-tighter">
              Reminder sent to {lastRecipients?.length || 0} student{lastRecipients?.length === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="border border-[#E5E7EB] max-h-[50vh] overflow-y-auto">
            {(lastRecipients || []).map((r) => (
              <div key={r.id} className="px-3 py-2 border-b border-[#E5E7EB] last:border-b-0 text-sm">
                <div className="font-medium">{r.name || "(no name)"}</div>
                <div className="text-xs text-[#525252]">{r.email}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setLastRecipients(null)} className="rounded-none bg-[#0A0A0A] hover:bg-[#1a1a1a]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function prettyBytes(n) {
  if (!n) return "0 B";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0;
  while (n >= 1024 && i < u.length - 1) { n /= 1024; i++; }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${u[i]}`;
}
